import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import './Tenants.css';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    has_esm61_meter: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'confirm' });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/get-tenants');
      const result = await response.json();
      if (result.success) {
        setTenants(result.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const url = editingTenant 
        ? `/api/update-tenant/${editingTenant.id}`
        : '/api/add-tenant';
      
      const method = editingTenant ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(editingTenant ? 'Tenant updated successfully!' : 'Tenant added successfully!');
        fetchTenants();
        closeModal();
      } else {
        throw new Error(result.error || 'Operation failed');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    setConfirmModal({
      isOpen: true,
      title: 'Archive Tenant',
      message: `Are you sure you want to archive ${tenant?.name}?\n\nThis tenant will no longer appear in the dropdown but their billing history will be preserved.`,
      type: 'confirm',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        doDelete(tenantId);
      }
    });
  };

  const doDelete = async (tenantId) => {
    try {
      const response = await fetch(`/api/delete-tenant/${tenantId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Tenant archived successfully!');
        fetchTenants();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      has_esm61_meter: false
    });
    setEditingTenant(null);
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (tenant) => {
    setFormData({
      name: tenant.name,
      address: tenant.address || '',
      phone: tenant.phone || '',
      has_esm61_meter: tenant.has_esm61_meter
    });
    setEditingTenant(tenant);
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingTenant(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="tenants-page">
      <div className="tenants-header">
        <h2>Tenant Management</h2>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Tenant
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="tenants-grid">
        {tenants.map(tenant => (
          <div key={tenant.id} className="tenant-card card">
            <div className="tenant-header">
              <h3>{tenant.name}</h3>
              {tenant.has_esm61_meter && (
                <span className="esm61-badge">ESM61</span>
              )}
            </div>
            
            <div className="tenant-details">
              {tenant.address && (
                <div className="tenant-field">
                  <strong>Address:</strong> {tenant.address}
                </div>
              )}
              {tenant.phone && (
                <div className="tenant-field">
                  <strong>Phone:</strong> {tenant.phone}
                </div>
              )}
              <div className="tenant-field">
                <strong>Created:</strong> {new Date(tenant.created_at).toLocaleDateString()}
              </div>
            </div>

            <div className="tenant-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => openEditModal(tenant)}
              >
                Edit
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDelete(tenant.id)}
              >
                Archive
              </button>
            </div>
          </div>
        ))}
      </div>

      {tenants.length === 0 && (
        <div className="empty-state">
          <p>No tenants found. Click "Add Tenant" to get started.</p>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTenant ? 'Edit Tenant' : 'Add New Tenant'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={formData.has_esm61_meter}
                    onChange={(e) => setFormData({...formData, has_esm61_meter: e.target.checked})}
                  />
                  Has ESM61 Meter
                </label>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <span className="loading"></span> : null}
                  {editingTenant ? 'Update' : 'Add'} Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default Tenants;